import { useLogout } from "@/hooks/auth/useLogout";
import { useRoleBasedNavigation } from "@/hooks/auth/useRoleBasedNavigation";
import { LogOut } from "lucide-react";
import { Link, useLocation } from "react-router";
import packageJson from "../../../package.json";
import CLLogo from "../../assets/logo/cl-logo-new.svg";
import CLLogoWhite from "../../assets/logo/cl-logo-white-new.svg";

import { useTranslation } from "react-i18next";
import { LanguageSelect } from "../shared/general/language-select";
import { Button } from "../ui/button";
import { GradientBorder } from "../ui/gradient-border";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import MobileNavigation from "./MobileNavigation";

interface UserHeaderProps {
  user: any | null;
  variant?: "default" | "glass";
}

const UserHeader: React.FC<UserHeaderProps> = ({
  user,
  variant = "default",
}) => {
  const { t } = useTranslation("common");
  const { handleLogout } = useLogout();
  const location = useLocation();
  const { userHeaderNavigationItems, getDefaultDashboard } =
    useRoleBasedNavigation();

  //varied styles based on variant prop
  const headerClass =
    variant === "glass" ? "glass-header" : "bg-white shadow-md";

  const renderNavigationItem = (item: any) => {
    const isActive =
      item.path === location.pathname ||
      (item.path !== "/dashboard" && location.pathname.startsWith(item.path));

    return (
      <Link
        key={item.id}
        to={item.path}
        className={`text-body1   hover:text-blue-dark-hover relative px-3 py-2   font-medium transition-colors duration-200 ${isActive
          ? variant === "glass"
            ? "text-blue-light-active underline underline-offset-5"
            : "text-blue-bg underline underline-offset-5"
          : variant === "glass"
            ? "text-white"
            : "text-blue-dark hover:text-blue-200"
          }`}
      >
        {t(`user.${item.id}`)}
      </Link>
    );
  };
  return (
    <header
      className={`max-w-screen-1600 sticky top-0 z-60 mx-auto flex justify-center rounded-full ${headerClass} shadow-md backdrop-blur-md`}
    >
      <GradientBorder shineColor={"white"} gradientDegree={0} />

      <div className=" w-full px-4 py-3 sm:px-6 sm:py-1">
        <div className="flex h-full items-center justify-between gap-2">
          <div className="flex items-center gap-6 md:gap-10">
            <div className="shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link to={getDefaultDashboard}>
                    <img
                      src={variant === "glass" ? CLLogoWhite : CLLogo}
                      alt="Company Logo"
                      className="sm:h-10 h-12 w-auto max-w-[150px] object-contain"
                    />
                  </Link>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Version {packageJson.version}
                    {import.meta.env.VITE_BUILD_INFO && (
                      <> ({import.meta.env.VITE_BUILD_INFO})</>
                    )}
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Navigation Items - Desktop */}
            <nav className="hidden items-center gap-4 xl:flex xl:gap-12">
              {userHeaderNavigationItems.map((item) =>
                renderNavigationItem(item),
              )}
            </nav>
          </div>

          {/* Desktop Actions */}
          <div className="hidden items-center gap-3 sm:gap-4 xl:flex">
            <div className="hidden xl:block">
              <LanguageSelect />
            </div>
            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              variant={"default"}
              size="sm"
              className=""
            >
              <span>{t("buttons.logout")}</span>
              <LogOut className="size-6" />
            </Button>
          </div>

          {/* Mobile Navigation */}
          <MobileNavigation className="xl:hidden" variant={variant} />
        </div>
      </div>
    </header>
  );
};

export default UserHeader;
